import * as React from 'react';
import { Platform, StyleSheet, View } from 'react-native';
import { translate } from 'secullum-i18n';
import {
  DropDown,
  ImageButton,
  isTablet,
  Modal,
  Space,
  Text
} from 'secullum-react-native-ui';
import {
  estiloBotaoDestaque,
  estiloTextoBotaoDestaque
} from '../../components/BotaoDestaque';
import { ButtonBar } from '../../components/ButtonBar';
import { getTheme } from '../../modules/layout';

export interface FiltroSolicitacaoOpcao {
  value: string;
  label: string;
}

export interface FiltrosSolicitacaoValores {
  empresa: string;
  departamento: string;
  funcao: string;
}

interface Props {
  open: boolean;
  filtros?: Partial<FiltrosSolicitacaoValores>;
  empresas: FiltroSolicitacaoOpcao[];
  departamentos: FiltroSolicitacaoOpcao[];
  funcoes: FiltroSolicitacaoOpcao[];
  onConcluir: (filtros: FiltrosSolicitacaoValores) => void;
  onCancelar: () => void;
}

class ModalFiltrosSolicitacao extends React.Component<
  Props,
  FiltrosSolicitacaoValores
> {
  state: FiltrosSolicitacaoValores = {
    empresa: '',
    departamento: '',
    funcao: ''
  };

  componentDidMount() {
    if (this.props.open) {
      this.sincronizarFiltros();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (!prevProps.open && this.props.open) {
      this.sincronizarFiltros();
      return;
    }

    if (this.props.open && prevProps.filtros !== this.props.filtros) {
      this.sincronizarFiltros();
    }
  }

  sincronizarFiltros = () => {
    const { filtros } = this.props;

    this.setState({
      empresa: filtros?.empresa ?? '',
      departamento: filtros?.departamento ?? '',
      funcao: filtros?.funcao ?? ''
    });
  };

  montarItensEmpresas = (): FiltroSolicitacaoOpcao[] => [
    { value: '', label: translate('Todas') },
    ...this.props.empresas
  ];

  montarItensDepartamentos = (): FiltroSolicitacaoOpcao[] => [
    { value: '', label: translate('Todos') },
    ...this.props.departamentos
  ];

  montarItensFuncoes = (): FiltroSolicitacaoOpcao[] => [
    { value: '', label: translate('Todas') },
    ...this.props.funcoes
  ];

  handleSelectChange = (
    name: keyof FiltrosSolicitacaoValores,
    value: string
  ) => {
    this.setState({
      [name]: value
    } as Pick<FiltrosSolicitacaoValores, keyof FiltrosSolicitacaoValores>);
  };

  handleModalConcluir = () => {
    const { empresa, departamento, funcao } = this.state;

    this.props.onConcluir({
      empresa,
      departamento,
      funcao
    });
  };

  render() {
    const { open, onCancelar } = this.props;
    const { empresa, departamento, funcao } = this.state;

    const plataformaWeb = Platform.OS === 'web';

    return (
      <Modal
        visible={open}
        onRequestClose={onCancelar}
        overlayStyle={plataformaWeb ? styles.modalWeb : styles.modal}
      >
        <View style={plataformaWeb ? styles.containerWeb : styles.container}>
          <View style={styles.header}>
            <Text style={styles.title}>{translate('Filtros Adicionais')}</Text>
            {plataformaWeb && (
              <ImageButton
                style={styles.imageButtonClose}
                icon="close"
                onPress={onCancelar}
              />
            )}
          </View>
          <View style={styles.body}>
            <DropDown
              nativeID="filtro-empresa"
              label={translate('Empresa')}
              value={empresa}
              items={this.montarItensEmpresas()}
              onChange={value => this.handleSelectChange('empresa', value)}
              emptyMessage={translate('Sua busca não retornou resultados')}
            />
            <Space height={16} />
            <DropDown
              nativeID="filtro-departamento"
              label={translate('Departamento')}
              value={departamento}
              items={this.montarItensDepartamentos()}
              onChange={value => this.handleSelectChange('departamento', value)}
              emptyMessage={translate('Sua busca não retornou resultados')}
            />
            <Space height={16} />
            <DropDown
              nativeID="filtro-funcao"
              label={translate('Função')}
              value={funcao}
              items={this.montarItensFuncoes()}
              onChange={value => this.handleSelectChange('funcao', value)}
              emptyMessage={translate('Sua busca não retornou resultados')}
            />
          </View>

          <ButtonBar
            buttonBarStyle={styles.buttons}
            leftButton={{
              text: translate('Cancelar'),
              onPress: onCancelar,
              primary: false,
              nativeID: 'botao-modal-filtro-solicitacao-cancelar'
            }}
            rightButton={{
              text: translate('Concluir'),
              onPress: this.handleModalConcluir,
              nativeID: 'botao-modal-filtro-solicitacao-concluir',
              style: estiloBotaoDestaque,
              textStyle: estiloTextoBotaoDestaque
            }}
          />
        </View>
      </Modal>
    );
  }
}

const theme = getTheme();

const styles = StyleSheet.create({
  modal: {
    flex: 1,
    justifyContent: 'center'
  },
  modalWeb: {
    overflow: 'scroll'
  },
  container: {
    backgroundColor: theme.backgroundColor1,
    marginHorizontal: 16,
    borderRadius: 3
  },
  containerWeb: {
    margin: 'auto',
    width: '100%',
    maxWidth: 550,
    backgroundColor: theme.backgroundColor1,
    borderRadius: 3
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12
  },
  title: {
    color: theme.textColor3,
    fontSize: isTablet() ? 20 : 17,
    fontFamily: theme.fontFamily1
  },
  imageButtonClose: {
    borderWidth: 0,
    margin: 0,
    padding: 0
  },
  body: {
    padding: 20
  },
  buttons: {
    paddingTop: 10,
    paddingRight: 16,
    paddingBottom: 16,
    paddingLeft: 16,
    justifyContent: 'flex-end',
    flexDirection: 'row'
  }
});

export default ModalFiltrosSolicitacao;
